/* import __COLOCATED_TEMPLATE__ from './workflow-selector.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type { AllChannels } from 'embercom/lib/operator/custom-bots/constants';
import { states } from 'embercom/models/data/matching-system/matching-constants';
import type { HandoverToWorkflowConfiguration } from 'embercom/models/fin-voice/number-configuration';

interface ReusableWorkflowWireFormat {
  id: string;
  title: string;
  operator_workflow_instance_id: number;
  state: number;
  user_visible: boolean;
  compatible_channels: AllChannels[];
}

export interface ReusableWorkflow {
  text: string;
  value: number;
  icon: string;
  workflowId: string;
  state: number;
  component: string;
  compatibleChannels: AllChannels[];
  userVisible: boolean;
  componentAttrs: {
    showTooltips: boolean;
    tooltipText: string | undefined;
  };
  live: boolean;
  isDisabled: boolean;
  isPhoneWorkflow: boolean;
}

interface Signature {
  Element: HTMLDivElement;
  Args: {
    onSelectWorkflow: (workflow: ReusableWorkflow | undefined) => void;
    handoverConfig?: HandoverToWorkflowConfiguration;
    readOnly?: boolean;
  };
}

export default class WorkflowSelector extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked workflows: ReusableWorkflow[] = [];
  @tracked selectorIsOpen = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.fetchTriggerableWorkflows).perform();
  }

  @task({ drop: true })
  *fetchPhoneWorkflows(
    includeCompatibleChannels = false,
  ): TaskGenerator<ReusableWorkflowWireFormat[]> {
    return yield ajax({
      url: `/ember/operator_custom_bots/phone_partial_bots`,
      type: 'GET',
      data: {
        app_id: this.appService.app.id,
        include_compatible_channels: includeCompatibleChannels,
      },
    });
  }

  @task({ drop: true })
  *fetchTriggerableWorkflows(): TaskGenerator<void> {
    let triggerableWorkflows: ReusableWorkflow[] = [];
    let reusableWorkflows = yield taskFor(this.fetchPhoneWorkflows).perform();

    reusableWorkflows.forEach((workflow: ReusableWorkflowWireFormat) => {
      triggerableWorkflows.push({
        text:
          workflow.title ||
          this.intl.t('operator.custom-bot.editor.no-trigger-bot-selector.untitled'),
        value: Number(workflow.id),
        icon: 'bot-filled',
        workflowId: workflow.id,
        state: workflow.state,
        component: 'operator-flows/editor/trigger-workflow/no-trigger-bot-option',
        compatibleChannels: workflow.compatible_channels,
        userVisible: workflow.user_visible,
        componentAttrs: {
          showTooltips: workflow.state !== states.live,
          tooltipText: this.getReusableWorkflowTooltipText(workflow),
        },
        live: workflow.state === states.live,
        isDisabled: workflow.state !== states.live,
        isPhoneWorkflow: true,
      });
    });

    // Sort by bot state in the order live, paused, draft
    let liveWorkflows = triggerableWorkflows.filter((workflow) => workflow.state === states.live);
    let pausedWorkflows = triggerableWorkflows.filter(
      (workflow) => workflow.state === states.paused,
    );
    let draftWorkflows = triggerableWorkflows.filter((workflow) => workflow.state === states.draft);
    this.workflows = [...liveWorkflows, ...pausedWorkflows, ...draftWorkflows];
  }

  getReusableWorkflowTooltipText(workflow: ReusableWorkflowWireFormat) {
    if (workflow.state !== states.live) {
      return this.intl.t('operator.custom-bot.editor.no-trigger-bot-selector.require-set-bot-live');
    }
    return undefined;
  }

  get hasWorkflows() {
    return this.workflows.length > 0;
  }

  get items() {
    if (this.loading) {
      return [
        {
          value: null,
          component: 'operator-flows/editor/trigger-workflow/loading-state',
          componentShouldReplaceItem: true,
        },
      ];
    }
    if (!this.hasWorkflows) {
      return [
        {
          value: null,
          component: 'operator-flows/editor/trigger-workflow/empty-state',
          componentShouldReplaceItem: true,
        },
      ];
    }
    return [
      {
        items: this.workflows,
      },
    ];
  }

  get loading() {
    return taskFor(this.fetchTriggerableWorkflows).isRunning;
  }

  get selectedWorkflow() {
    let selectedValue = this.args.handoverConfig?.config.workflowId;

    if (selectedValue) {
      return this.workflows.find((wf) => wf.value === Number(selectedValue));
    }
    return undefined;
  }

  get selectedValue() {
    if (this.loading || !this.selectedWorkflow) {
      return undefined;
    }

    return Number(this.selectedWorkflow?.value);
  }

  get hasError() {
    return (
      (!this.loading && !this.args.handoverConfig?.config.workflowId) ||
      !this.selectedWorkflow?.live
    );
  }

  @action
  async setTriggerableWorkflowId(workflowId: string | undefined) {
    if (workflowId) {
      let workflows = await taskFor(this.fetchPhoneWorkflows).perform(true);
      let workflow = this.workflows.find((wf) => wf.value === Number(workflowId));
      if (workflow) {
        workflow.compatibleChannels = workflows[0]?.compatible_channels;
        this.args.onSelectWorkflow?.(workflow);
      }
    } else {
      this.args.onSelectWorkflow?.(undefined);
    }
  }

  @action
  onSelectorOpen() {
    taskFor(this.fetchTriggerableWorkflows).perform();
    this.selectorIsOpen = true;
  }

  @action
  onSelectorClose() {
    this.selectorIsOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::NumberConfiguration::PhoneHandover::WorkflowSelector': typeof WorkflowSelector;
  }
}
